import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { safeStorage } from '../utils/safeStorage';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

export const auth = {
  getAccessToken: () => safeStorage.getItem('tf_access_token'),
  getRefreshToken: () => safeStorage.getItem('tf_refresh_token'),
  getCompanyId: () => safeStorage.getItem('tf_company_id'),
  setTokens: (access: string, refresh: string) => {
    safeStorage.setItem('tf_access_token', access);
    safeStorage.setItem('tf_refresh_token', refresh);
  },
  setCompanyId: (id: string | number | null) => {
    if (id == null) safeStorage.removeItem('tf_company_id');
    else safeStorage.setItem('tf_company_id', String(id));
  },
  clear: () => {
    safeStorage.removeItem('tf_access_token');
    safeStorage.removeItem('tf_refresh_token');
    safeStorage.removeItem('tf_company_id');
  },
};

/**
 * Returns true when the access token exists, parses as a JWT, and has not expired.
 * Used by route guards to decide between rendering the protected tree or
 * redirecting to /login *before* any API call fires (no flash of protected UI).
 */
export function hasValidAccessToken(): boolean {
  const token = auth.getAccessToken();
  if (!token) return false;
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  try {
    const padded = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(
      atob(padded + '=='.slice(0, (4 - (padded.length % 4)) % 4)),
    );
    if (typeof payload.exp !== 'number') return false;
    // Allow 5s of clock skew; treat anything else as expired.
    return payload.exp * 1000 > Date.now() - 5000;
  } catch {
    return false;
  }
}

api.interceptors.request.use((config) => {
  const token = auth.getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const companyId = auth.getCompanyId();
  if (companyId) config.headers['X-Company-Id'] = companyId;
  return config;
});

let refreshing: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = auth.getRefreshToken();
  if (!refreshToken) return null;
  try {
    const resp = await axios.post(`${BASE_URL}/auth/refresh`, { refreshToken });
    const { accessToken, refreshToken: newRefresh } = resp.data;
    auth.setTokens(accessToken, newRefresh);
    return accessToken;
  } catch {
    auth.clear();
    return null;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
    const status = error.response?.status;

    if (status === 401 && original && !original._retry) {
      original._retry = true;
      if (!refreshing) refreshing = refreshAccessToken();
      const newToken = await refreshing;
      refreshing = null;
      if (newToken) {
        original.headers = original.headers || {};
        (original.headers as any).Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;
